import styled, { keyframes } from 'styled-components';
import { 
  PageContainer, 
  Container, 
  PageTitle, 
  FormInput, 
  FormSelect,
  Card,
  Button 
} from './index';

// Space-themed animation keyframes
const glowPulse = keyframes`
  0% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
  50% { box-shadow: 0 0 15px rgba(99, 102, 241, 0.5), inset 0 0 8px rgba(99, 102, 241, 0.2); }
  100% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0); }
`;

// Enhanced Page Container with space background
export const SpacePageContainer = styled(PageContainer)`
  background: transparent;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
    opacity: 0.15;
    background-image: 
      radial-gradient(2px 2px at 40px 60px, rgba(255, 255, 255, 0.9), rgba(0, 0, 0, 0)),
      radial-gradient(2px 2px at 100px 150px, rgba(255, 255, 255, 0.9), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 200px 80px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0)),
      radial-gradient(2px 2px at 300px 250px, rgba(255, 255, 255, 0.9), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 400px 100px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 500px 200px, rgba(255, 255, 255, 0.7), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 50px 300px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 250px 350px, rgba(255, 255, 255, 0.7), rgba(0, 0, 0, 0)),
      radial-gradient(1px 1px at 550px 350px, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0));
  }
`;

// Space-themed container
export const GlassContainer = styled(Container)`
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  background: rgba(15, 23, 42, 0.25);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  padding: 1.5rem;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
`;

// Glowing page title
export const GlowingPageTitle = styled(PageTitle)`
  color: #fff;
  text-shadow: 0 0 10px rgba(99, 102, 241, 0.7);
  position: relative;
  margin-bottom: 1.5rem;
  font-weight: 700;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.8), rgba(124, 58, 237, 0.8));
    border-radius: 3px;
    animation: ${glowPulse} 3s ease-in-out infinite;
  }
`;

// Filter bar
export const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(99, 102, 241, 0.15);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const SearchInput = styled(FormInput)`
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
    outline: none;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

export const StatusFilter = styled(FormSelect)`
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  appearance: none;
  padding-right: 2.5rem;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='rgba(255, 255, 255, 0.7)' viewBox='0 0 16 16'><path d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/></svg>");
  background-repeat: no-repeat;
  background-position: calc(100% - 1rem) center;
  
  &:hover {
    background-color: rgba(15, 23, 42, 0.6);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.2);
  }
  
  &:focus {
    background-color: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
    outline: none;
  }
`;

// Company card components
export const CompanyCard = styled(Card)`
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  transition: all 0.3s ease;
  margin-bottom: 1.2rem;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
    
    &:before {
      opacity: 1;
    }
  }
  
  /* Animated glow effect on hover */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      120deg,
      transparent, 
      transparent,
      rgba(99, 102, 241, 0.05),
      transparent,
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
`;

export const CompanyLogo = styled.div`
  width: 65px;
  height: 65px;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(99, 102, 241, 0.3);
  background: rgba(15, 23, 42, 0.6);
  position: relative;
  transition: all 0.3s ease;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.3s ease;
  }
  
  /* Shiny effect on top */
  &:after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transform: rotate(45deg);
    transition: opacity 0.3s ease;
    opacity: 0.5;
  }
  
  ${CompanyCard}:hover & {
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.4);
    border-color: rgba(99, 102, 241, 0.5);
    
    &:after {
      opacity: 1;
    }
    
    img {
      transform: scale(1.1);
    }
  }
`;

// Company detail components
export const DetailItem = styled.div`
  margin-bottom: 1.2rem;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  
  strong {
    display: inline-block;
    min-width: 150px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 600;
    font-size: 0.9rem;
  }
  
  span {
    color: rgba(255, 255, 255, 0.95);
    font-size: 0.95rem;
  }
`;

export const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem 1rem;
  background: rgba(15, 23, 42, 0.4);
  border-radius: 10px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  margin-bottom: 0.8rem;
  transition: all 0.3s ease;
  position: relative;
  cursor: pointer;
  
  /* Glow border on hover */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 10px;
    padding: 1.5px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), rgba(79, 70, 229, 0.7));
    -webkit-mask: 
      linear-gradient(#fff 0 0) content-box, 
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px) translateX(2px);
    background: rgba(15, 23, 42, 0.6);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    border-color: rgba(99, 102, 241, 0.4);
    
    &:before {
      opacity: 1;
    }
  }
`;

export const DocumentIcon = styled.div`
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(79, 70, 229, 0.8));
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.3rem;
  box-shadow: 0 0 15px rgba(99, 102, 241, 0.4);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  /* Shiny effect */
  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transform: rotate(45deg);
    animation: ${glowPulse} 3s infinite ease-in-out;
  }
  
  ${DocumentCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

// Modal components
export const CompanyModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 2rem;
`;

export const ModalCard = styled(Card)`
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  overflow: hidden;
  width: calc(100% - 4rem);
  max-width: 800px;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  
  &::-webkit-scrollbar {
    width: 8px;
    background: rgba(15, 23, 42, 0.3);
    border-radius: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.5);
    border-radius: 8px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.7);
    }
  }
  
  animation: ${floatAnimation} 4s infinite ease-in-out;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(
      90deg, 
      rgba(99, 102, 241, 0.5), 
      rgba(79, 70, 229, 0.5),
      rgba(99, 102, 241, 0.1)
    );
  }
  
  h2 {
    margin: 0;
    color: #fff;
    text-shadow: 0 0 10px rgba(99, 102, 241, 0.5);
    font-size: 1.6rem;
    font-weight: 700;
  }
`;

export const CloseButton = styled.button`
  background: rgba(99, 102, 241, 0.2);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  
  /* Shiny effect */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.7s ease;
  }
  
  &:hover {
    background: rgba(99, 102, 241, 0.3);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.4);
    transform: translateY(-2px);
    
    &:before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Action button
export const ActionButton = styled(Button)`
  background: rgba(79, 70, 229, 0.7);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  padding: 0.7rem 1.2rem;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  font-weight: 600;
  font-size: 0.95rem;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.7s ease;
  }
  
  &:hover {
    background: rgba(79, 70, 229, 0.9);
    transform: translateY(-3px);
    box-shadow: 0 0 20px rgba(99, 102, 241, 0.5);
    
    &:before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: rgba(79, 70, 229, 0.3);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    opacity: 0.7;
    
    &:before {
      display: none;
    }
  }
`;

// Pagination
export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 0.5rem;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 12px;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  border: 1px solid rgba(99, 102, 241, 0.15);
`;

export const PageButton = styled.button`
  background: ${props => props.active ? 'rgba(99, 102, 241, 0.8)' : 'rgba(15, 23, 42, 0.6)'};
  color: white;
  border: 1px solid ${props => props.active ? 'rgba(99, 102, 241, 0.6)' : 'rgba(99, 102, 241, 0.2)'};
  border-radius: 6px;
  padding: 0.5rem 0.8rem;
  min-width: 2.5rem;
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: rgba(99, 102, 241, 0.7);
    border-color: rgba(99, 102, 241, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

// Notification
export const Notification = styled.div`
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 5600;
  padding: 1.2rem 1.5rem;
  border-radius: 12px;
  background: ${props => props.type === 'success' 
    ? 'rgba(16, 185, 129, 0.95)' 
    : props.type === 'error'
      ? 'rgba(239, 68, 68, 0.95)'
      : 'rgba(79, 70, 229, 0.95)'};
  color: white;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(15px);
  border: 1px solid ${props => props.type === 'success' 
    ? 'rgba(16, 185, 129, 0.6)' 
    : props.type === 'error'
      ? 'rgba(239, 68, 68, 0.6)'
      : 'rgba(79, 70, 229, 0.6)'};
  z-index: 5300;
  max-width: 400px;
  animation: ${floatAnimation} 3s infinite ease-in-out;
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.3px;
  
  &:before {
    content: '${props => props.type === 'success' 
      ? '✓' 
      : props.type === 'error' 
        ? '✗'
        : 'ℹ'}';
    display: inline-block;
    margin-right: 10px;
    font-weight: bold;
  }
`;

// Empty state card
export const EmptyCard = styled(Card)`
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;
